import { del, executeApp, get, post, put } from '@/utils/request'
import { AGENTS, AGENT_SKILL, AGENT_SKILL_MAPPING, IMAGE_SHARE_COMMUNITY, WORKSPACE } from '@/api/defineRouting'

export function getListAgentByFolder<T = any>(
  data: { status?: string; workspace_folder_id?: number; page: number; page_size: number; industrys?: string[] },
) {
  const { industrys, ...rest } = data
  const searchParams = new URLSearchParams()

  // Add other parameters
  Object.entries(rest).forEach(([key, value]) => {
    if (value !== undefined)
      searchParams.append(key, String(value))
  })

  // Add industries
  if (industrys?.length) {
    industrys.forEach((industry) => {
      searchParams.append('industry', industry)
    })
  }

  return get<T>({
    url: `/agents-service/agents/list?${searchParams.toString()}`,
  })
}

export function getListAgent<T = any>(
  data: { workspace_id: number },
) {
  return get<T>({
    url: `/agents-service/agents/get-lst-group-by-folder?workspace_id=${data.workspace_id}`,
    data,
  })
}

export function detailKnowledge<T = any>(data: any) {
  return get<T>({
    url: `${AGENTS.DETAIL}/${data.agent_id}`,
  },
  )
}

export function getDetailAgent<T = any>(data: {
  agent_id: number
}) {
  return get<T>({
    url: `/agents-service/agents/${data.agent_id}`,
  })
}

export function createAgent<T = any>(data: {
  agent_name: string
  job_description: string
  language: string
  language_name: string
  model: string | null
  welcome_message: string
  message_placeholder: string
  suggested_messages: string[]
  workspace_folder_id: number
  temperature?: number
  top_p?: number
  presence_penalty?: number
  frequency_penalty?: number
  max_tokens?: number
  job_title: string
  instruction: string
}) {
  return post<T>({
    url: '/agents-service/agents/create',
    data,
  })
}

export function createAgentV2<T = any>(data: {
  agent_name: string
  job_description: string
  language: string
  language_name: string
  model: string | null
  welcome_message: string
  message_placeholder: string
  suggested_messages: string[]
  workspace_folder_id: number
  temperature?: number
  top_p?: number
  presence_penalty?: number
  frequency_penalty?: number
  max_tokens?: number
  job_title: string
  instruction: string
}) {
  return post<T>({
    url: '/agents-service/agents/create_v2',
    data,
  })
}

export function updateAgent<T = any>(data: {
  agent_id: number
  agent_name: string
  agent_theme: {
    agent_id: number
    theme_id?: number
    bg_img?: number | null | undefined | string
    bg_img_id?: number | null | undefined
    text_color?: string
    bubble_color_ai?: string
    bubble_color_client?: string
    bg_color?: string | null
  }
  job_description: string
  language: string
  language_name: string
  model: string
  welcome_message: string
  message_placeholder: string
  suggested_messages: string[]
  workspace_folder_id: number
  job_title: string
  instruction: string
  work_experience: string[]
  description: string
  temperature?: number
  top_p?: number
  presence_penalty?: number
  frequency_penalty?: number
  max_tokens?: number
  model_param: string
  agent_images?: boolean
  agent_document?: boolean
  co_authors: {
    author_avatar_id: number
    name: string
    position: string
    description: string
    avatar: string
  }[]
}) {
  return put<T>({
    url: `/agents-service/agents/update/${data.agent_id}`,
    data,
  })
}

export function updateAgentV2<T = any>(data: {
  agent_id: number
  agent_name: string
  agent_theme: {
    agent_id: number
    theme_id?: number
    bg_img?: number | null | undefined | string
    bg_img_id?: number | null | undefined
    text_color?: string
    bubble_color_ai?: string
    bubble_color_client?: string
    bg_color?: string | null
  }
  job_description: string
  language: string
  language_name: string
  model: string
  welcome_message: string
  message_placeholder: string
  suggested_messages: string[]
  workspace_folder_id: number
  job_title: string
  instruction: string
  work_experience: string[]
  description: string
  temperature?: number
  top_p?: number
  presence_penalty?: number
  frequency_penalty?: number
  max_tokens?: number
  model_param: string
  co_authors: {
    author_avatar_id: number
    name: string
    position: string
    description: string
    avatar: string
  }[]
}) {
  return put<T>({
    url: `/agents-service/agents/update_v2/${data.agent_id}`,
    data,
  })
}

export function deleteAgent<T = any>(data: {
  agent_id: number
}) {
  return del<T>({
    url: `/agents-service/agents/delete/${data.agent_id}`,
  })
}

export function updateDocVision<T = any>(data: {
  agent_id: number | string
  mode: boolean
}) {
  return put<T>({
    url: `/agents-service/agents/doc-vision/${data.agent_id}?mode=${data.mode}`,
  })
}

export function publishAgent<T = any>(data: {
  agent_id: number
}) {
  return post<T>({
    url: `/agents-service/agents/publish/${data.agent_id}`,
  })
}

export function unpublishAgent<T = any>(data: {
  agent_id: number
}) {
  return put<T>({
    url: `/agents-service/agents/unpublish/${data.agent_id}`,
  })
}

export function getListAgentMarket<T = any>(data: {
  status?: string
  workspace_id?: number
  page: number
  page_size: number
}) {
  return get<T>({
    url: '/agents-service/agents/marketplace/list',
    data,
  })
}

export function updateAgentMarket<T = any>(data: {
  id: number
  description: string
  work_experience: string[]
}) {
  return put<T>({
    url: '/agents-service/agents/update-description',
    data,
  })
}

export function pullAgentToFolder<T = any>(data: {
  agent_id: number
  workspace_folder_id: number | null
}) {
  return post<T>({
    url: '/agents-service/agents/pull-agent',
    data,
  })
}

export function pushBackAgentToFolder<T = any>(data: {
  agent_id: number
  workspace_folder_id: number | null | string
}) {
  return post<T>({
    url: '/agents-service/agents/push-agent',
    data,
  })
}

// Monitoring Agent
export function getMonitorAgent<T = any>(
  data: { id: string; start_date: string; end_date: string },
) {
  return get<T>({
    url: `/agents-service/agents/monitor/${data.id}?start_date=${data.start_date}&end_date=${data.end_date}`,
  })
}

export function getListAgentModel<T = any>() {
  return get<T>({
    url: '/dify-service/dify-config/get-lst-llm',
  })
}

export function getListAgentModelParameter<T = any>(data: {
  model: string
}) {
  return get<T>({
    url: `/dify-service/dify-config/get-parameter-rules-by-model?model=${data.model}`,
  })
}

export function getListAgentModelLlm<T = any>() {
  return post<T>({
    url: '/agents-service/model-llm/get-lst',
  })
}

// Rate Agent
export function createAgentComment<T = any>(data: {
  agent_id: number
  rating: number
  comment: string
}) {
  return post<T>({
    url: '/agents-service/agent-comment/create',
    data,
  })
}

export function updateAgentComment<T = any>(data: {
  id: number
  rating: number
  comment: string
}) {
  return post<T>({
    url: '/agents-service/agent-comment/update',
    data,
  })
}

export function deleteAgentComment<T = any>(data: {
  comment_id: number
}) {
  return get<T>({
    url: `/agents-service/agent-comment/delete?comment_id=${data.comment_id}`,
  })
}

export function getListAgentComment<T = any>(data: {
  agent_id: number
  page: number
  page_size: number
}) {
  return post<T>({
    url: '/agents-service/agent-comment/search',
    data,
  })
}

export function getRatingAgent<T = any>(data: {
  agent_id: number
}) {
  return get<T>({
    url: `/agents-service/agent-comment/get-rating-info?agent_id=${data.agent_id}`,
  })
}

// co author
export function createCoAuthor<T = any>(data: {
  agent_id: string
  name: string
  position: string
  description: string
  file_id: number
}) {
  return post<T>({
    url: '/agents-service/co-author-agent/create',
    data,
  })
}

export function updateCoAuthor<T = any>(data: {
  name: string
  position: string
  description: string
  file_id: number
  agent_id: number
  record_id: number
}) {
  return put<T>({
    url: `/agents-service/co-author-agent/${data.record_id}`,
    data,
  })
}

export function deleteCoAuthor<T = any>(data: {
  id: number
}) {
  return del<T>({
    url: `/agents-service/co-author-agent/${data.id}`,
  })
}

export function getListCoAuthor<T = any>(data: {
  agent_id: number
  page: number
  page_size: number
}) {
  return post<T>({
    url: '/agents-service/co-author-agent/search',
    data,
  })
}

/**
 * Agent Task
 */

interface GetListAgentTaskRequest {
  parent_task: {
    template_id: number
    source_type: string
    source_id: number | null
    lst_task_field: any[]
  }
  children_tasks: [
    {
      lst_task_field: any[]
    },
  ]
}

export function getListAgentTask<T = any>(data: GetListAgentTaskRequest) {
  return executeApp<T>(AGENTS.GET_LIST_AGENT_TASK, data, 'post',
  )
}

/**
 * Skill
 */
export function getListSkillAgent<T = any>(data: any) {
  return executeApp<T>(AGENT_SKILL.LIST, data, 'get',
  )
}

/**
 * Skill Mapping
 */
export function getListSkillMappingAgent<T = any>(data: any) {
  return executeApp<T>(AGENT_SKILL_MAPPING.LIST, data, 'post',
  )
}

export function assignSkillToAgent<T = any>(data: any) {
  return executeApp<T>(AGENT_SKILL_MAPPING.ASSIGN_SKILL_TO_AGENT, data, 'post',
  )
}

export function createAgentFromPromptText<T = any>(data: any) {
  return executeApp<T>(AGENTS.GENERATE_AGENT, data, 'post',
  )
}

export function getListDefaultClassroomAgent<T = any>(data: any) {
  return executeApp<T>(AGENTS.GET_LST_DEFAULT_CLASSROOM_AGENT, data, 'post',
  )
}

export function pullDefaultClassroomAgents<T = any>(data: any) {
  return executeApp<T>(AGENTS.PULL_DEFAULT_CLASSROOM_AGENTS, data, 'post',
  )
}

export function getListDefaultClassroomAgentForUser<T = any>(data: any) {
  return executeApp<T>(AGENTS.GET_LST_DEFAULT_CLASSROOM_AGENT_FOR_USER, data, 'post',
  )
}

/**
 *
 */

export function shareImageShareCommunity<T = any>(data: any) {
  return executeApp<T>(IMAGE_SHARE_COMMUNITY.SHARE, data, 'post',
  )
}
export function approveImageShareCommunity<T = any>(data: any) {
  return executeApp<T>(IMAGE_SHARE_COMMUNITY.APPROVE, data, 'put',
  )
}
export function listImageShareCommunity<T = any>(data: any) {
  return executeApp<T>(IMAGE_SHARE_COMMUNITY.LIST, data, 'post',
  )
}
export function detailImageShareCommunity<T = any>(data: any) {
  return executeApp<T>(IMAGE_SHARE_COMMUNITY.DETAIL, data, 'get',
  )
}
export function likeImageShareCommunity<T = any>(data: any) {
  return executeApp<T>(`${IMAGE_SHARE_COMMUNITY.LIKE}/${data.share_id}`, {}, 'post',
  )
}

export function getListAllWorkspace<T = any>(data: any) {
  return executeApp<T>(WORKSPACE.LIST_ALL_WORKSPACE, data, 'post',
  )
}
